'use client';

import { createContext, useContext, type ReactNode } from 'react';
import type { ArticleBlock } from './types';
import type { StrapiPagination, StrapiQuerySpec } from './strapi';

/**
 * Host adapter contract for wiki-bound blocks and the wiki system
 * pages ({@code WikiIndexLayout}, {@code WikiDetailLayout},
 * {@code WikiTreeBlock}).
 *
 * <p>Backend: the gobito wiki module ({@code WikiPageImpl} +
 * {@code wiki-portal-ws}) serves this contract at
 * {@code /v1/public/{tenantId}/wiki/page/*} — server-computed tree,
 * by-path detail, search. The portal's {@code createLmsWikiSource}
 * adapts it via the {@code /api/wiki/*} BFF (locale resolved
 * server-side).
 *
 * <p>Shape ported verbatim from ThemeMaker's {@code lib/app/cms.ts}.
 * When no provider is mounted, {@link useWikiSource} returns
 * {@code null}; consuming blocks render a placeholder.
 */

/**
 * Wiki page shape. {@code parentSlug} self-references to build the
 * hierarchy; {@code order} is the per-parent display order.
 * {@code blocks?} threads per-page block overrides
 * ({@code WikiPageBlockOverride} entity) into the detail layout.
 */
export interface WikiPage {
  id: number;
  documentId?: string;
  slug: string;
  parentSlug: string | null;
  order: number;
  title: string;
  excerpt: string;
  body: string;
  publishedAt: string;
  updatedAt: string;
  blocks?: ArticleBlock[];
}

/**
 * Tree node shape — deliberately doesn't extend {@link WikiPage}.
 * The tree endpoint returns nested plain JSON (no Strapi envelope)
 * so {@code id} isn't reliable; consumers key off {@code slug}.
 */
export interface WikiTreeNode {
  slug: string;
  parentSlug: string | null;
  order: number;
  title: string;
  excerpt: string;
  body: string;
  publishedAt: string;
  updatedAt: string;
  children: WikiTreeNode[];
}

export type WikiPagination = StrapiPagination;
export type WikiQuerySpec = StrapiQuerySpec;

export interface WikiSource {
  /** Flat paginated listing. Used by {@code WikiTreeBlock} list
   *  layout + the search page. Default sort by
   *  {@code ['order:asc', 'title:asc']}. */
  listPages(spec?: WikiQuerySpec): Promise<{
    items: WikiPage[];
    pagination: WikiPagination;
  }>;

  /** Hierarchical tree, server-pre-computed. Returns {@code []} when
   *  the host has no wiki content. */
  getTree(): Promise<WikiTreeNode[]>;

  /** Single page by slug. {@code slug} may include {@code /} for
   *  nested pages; hosts must accept the slash-joined path. */
  getPageBySlug(slug: string): Promise<WikiPage | null>;
}

/**
 * Pure helper: DFS the tree to find the path root → matching node
 * for breadcrumb rendering. Not on {@link WikiSource} because it's
 * pure traversal — hosts shouldn't have to re-implement it.
 */
export function findWikiPath(tree: WikiTreeNode[], slug: string): WikiTreeNode[] {
  for (const node of tree) {
    if (node.slug === slug) return [node];
    if (node.children.length > 0) {
      const sub = findWikiPath(node.children, slug);
      if (sub.length > 0) return [node, ...sub];
    }
  }
  return [];
}

const WikiSourceContext = createContext<WikiSource | null>(null);

export function WikiSourceProvider({
  value,
  children,
}: {
  value: WikiSource;
  children: ReactNode;
}) {
  return <WikiSourceContext.Provider value={value}>{children}</WikiSourceContext.Provider>;
}

/** Returns the host-provided source, or {@code null} when no
 *  provider is mounted. */
export function useWikiSource(): WikiSource | null {
  return useContext(WikiSourceContext);
}
